import * as PusherPushNotifications from "@pusher/push-notifications-web";

class PusherBeamsService {
  constructor() {
    this.beamsClient = null;
    this.instanceId = null;
    this.isInitialized = false;
    this.deviceId = null;
  }

  /**
   * Verificar si el navegador soporta notificaciones
   */
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  /**
   * Verificar si estamos en HTTPS (requerido para Service Workers)
   */
  isSecureContext() {
    return window.location.protocol === 'https:' || 
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  }

  /**
   * Inicializar Pusher Beams
   */
  async initialize(instanceId) {
    if (this.isInitialized) {
      console.log('⚠️ Pusher Beams ya está inicializado');
      return true;
    }

    // Verificaciones previas
    if (!this.isSupported()) {
      console.error('❌ Este navegador no soporta notificaciones push');
      return false;
    }

    if (!this.isSecureContext()) {
      console.error('❌ Pusher Beams requiere HTTPS');
      return false;
    }

    if (!instanceId) {
      console.error('❌ Instance ID no proporcionado');
      return false;
    }

    try {
      this.instanceId = instanceId;

      console.log('🔄 Inicializando Pusher Beams...');
      console.log('   Instance ID:', instanceId);

      // Crear cliente - NO registrar Service Worker manualmente
      // Pusher Beams lo hace automáticamente
      this.beamsClient = new PusherPushNotifications.Client({
        instanceId: instanceId,
      });

      this.isInitialized = true;
      console.log('✅ Cliente Pusher Beams creado');

      return true;
    } catch (error) {
      console.error('❌ Error inicializando Pusher Beams:', error);
      this.isInitialized = false;
      return false;
    }
  }

  /**
   * Esperar a que el Service Worker esté completamente activo
   */
  async waitForServiceWorker(timeout = 10000) {
    console.log('⏳ Esperando a que Service Worker esté listo...');

    const startTime = Date.now();

    while (Date.now() - startTime < timeout) {
      const registration = await navigator.serviceWorker.getRegistration();

      if (registration) {
        // Verificar si hay un SW activo O activating
        const sw = registration.active || registration.installing || registration.waiting;

        if (registration.active) {
          console.log('✅ Service Worker está activo y listo');
          return true;
        }

        if (sw) {
          console.log('⏳ Service Worker encontrado, esperando activación...', sw.state);

          // Esperar a que cambie de estado
          await new Promise(resolve => {
            sw.addEventListener('statechange', function handler(e) {
              console.log('🔄 SW cambió de estado:', e.target.state);
              if (e.target.state === 'activated') {
                sw.removeEventListener('statechange', handler);
                resolve();
              }
            });

            // Timeout de seguridad
            setTimeout(resolve, 1000);
          });
        }
      }

      // Esperar un poco antes de volver a verificar
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    console.warn('⚠️ Timeout esperando Service Worker');
    return false;
  }

  /**
   * Deja el dispositivo listo para asociarse a `identidad`.
   *
   * `setUserId()` sobre un dispositivo que ya tiene OTRO user id asociado no
   * cambia nada: el SDK lo rechaza. Y todos los dispositivos registrados hasta
   * hoy tienen el uuid pelado, que es justamente el que estamos dejando de usar.
   * `clearAllState()` + `start()` es la migración que documenta Pusher.
   *
   * Si el SDK no expone `getUserId` (versiones viejas), se limpia igual: el costo
   * es un re-registro de más y el beneficio es no quedar con Beams muerto.
   */
  async migrarSiCambioLaIdentidad(identidad) {
    try {
      const actual = typeof this.beamsClient.getUserId === 'function'
        ? await this.beamsClient.getUserId()
        : null;

      if (!actual || actual === identidad) {
        return;
      }

      console.log(`🔄 El dispositivo estaba asociado a "${actual}", se limpia para asociarlo a "${identidad}"`);

      await this.beamsClient.clearAllState();
      await this.beamsClient.start();
      this.deviceId = await this.beamsClient.getDeviceId();
    } catch (error) {
      // Que la migración falle no puede tumbar el registro: si el setUserId de
      // después también falla, ahí sí se corta, y con el error real a la vista.
      console.warn('⚠️ No se pudo limpiar el estado previo de Beams:', error?.message || error);
    }
  }

  /**
   * Registrar el dispositivo como este usuario autenticado de Beams.
   *
   * @param {string} identidad - Canal del usuario, con el tenant adentro
   *                             (`storarmega.<uuid>`). NO el uuid pelado: el uuid
   *                             se repite entre tenants y Beams es una sola
   *                             instancia compartida, así que dos personas de
   *                             tenants distintos quedaban siendo el mismo
   *                             usuario de Beams.
   * @param {string} accessToken - Token de acceso para autenticación con el backend
   */
  async start(identidad, accessToken = null) {
    if (!this.beamsClient) {
      console.error('❌ Pusher Beams no está inicializado. Llama a initialize() primero.');
      return false;
    }

    if (!identidad) {
      console.error('❌ Canal de notificaciones no proporcionado');
      return false;
    }

    // Sin token no hay forma segura de registrarse: el fallback que había,
    // suscribirse a un Device Interest, es público por diseño (cualquiera que
    // sepa el nombre se suscribe desde su navegador) y uno de los intereses era
    // `all-users`, que alcanzaba a los dispositivos de TODOS los tenants.
    if (!accessToken) {
      console.warn('⚠️ Pusher Beams: sin token de acceso, no se registra el dispositivo');
      return false;
    }

    try {
      // CRÍTICO: Esperar a que el Service Worker esté completamente activo
      // antes de intentar registrar el dispositivo
      const swReady = await this.waitForServiceWorker();

      if (!swReady) {
        console.error('❌ Service Worker no está listo después del timeout');
        return false;
      }

      console.log('🔄 Registrando dispositivo en Pusher Beams...');

      // Esto solicita permisos de notificación Y registra el dispositivo
      await this.beamsClient.start();

      console.log('✅ Dispositivo registrado exitosamente');

      // El SDK NO deja cambiar el user id de un dispositivo ya asociado: tira
      // error y el dispositivo queda inutilizable para Beams. Pasa siempre en el
      // primer arranque después de este cambio, porque todos los dispositivos
      // están registrados con el uuid pelado. Se limpia el estado y se vuelve a
      // arrancar, que es la migración que recomienda Pusher.
      await this.migrarSiCambioLaIdentidad(identidad);

      // Obtener Device ID para debugging
      this.deviceId = await this.beamsClient.getDeviceId();

      // AUTENTICACIÓN CON BACKEND
      console.log('🔐 Autenticando usuario con backend...');

      try {
        // Llamar al endpoint de autenticación del backend
        // IMPORTANTE: Token provider personalizado con POST explícito
        const beamsTokenProvider = {
          fetchToken: async (userId) => {
            console.log('📡 Llamando al backend con POST para obtener token...');

            const response = await fetch(`${window.location.origin}/api/backoffice/pusher/beams-auth?user_id=${encodeURIComponent(userId)}`, {
              method: 'POST', // Forzar POST
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
            });

            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ Error del backend:', response.status, errorText);
              throw new Error(`Backend error: ${response.status}`);
            }

            const data = await response.json();
            console.log('✅ Token recibido del backend');

            return data;
          }
        };

        // Autenticar usuario - Esto automáticamente lo suscribe
        await this.beamsClient.setUserId(identidad, beamsTokenProvider);

        console.log('✅ Usuario autenticado en Pusher Beams');
      } catch (error) {
        // Antes acá había un fallback a Device Interests. Se sacó: los intereses
        // son públicos y uno de ellos era `all-users`, o sea los dispositivos de
        // todos los tenants. Si la autenticación falla, no hay push y listo.
        console.error('❌ Error autenticando usuario con backend:', error);
        console.error('   Error details:', error.message);

        return false;
      }

      // Información para testing
      console.log('\n' + '='.repeat(80));
      console.log('🎯 PUSHER BEAMS - INFORMACIÓN PARA PRUEBAS');
      console.log('='.repeat(80));
      console.log(`📱 Device ID: ${this.deviceId}`);
      console.log(`👤 User ID: ${identidad}`);
      console.log('='.repeat(80));
      console.log('💡 Cómo enviar notificación de prueba:');
      console.log('   1. Abre: https://dashboard.pusher.com/beams');
      console.log('   2. Selecciona tu instancia');
      console.log('   3. Ve a "Debug Console"');
      console.log(`   4. En "Publish to Device", pega: ${this.deviceId}`);
      console.log('   5. Escribe título y cuerpo, y envía');
      console.log('='.repeat(80) + '\n');

      return true;
    } catch (error) {
      console.error('❌ Error registrando dispositivo:', error);
      console.error('   Tipo de error:', error.name);
      console.error('   Mensaje:', error.message);

      // Ayuda específica según el tipo de error
      if (error.message && error.message.includes('permission')) {
        console.error('⚠️ El usuario denegó los permisos de notificación');
        console.error('   Solución: Habilita las notificaciones en la configuración del navegador');
      } else if (error.name === 'AbortError') {
        console.error('⚠️ Error de registro del Service Worker');
        console.error('   Solución: Verifica que /service-worker.js esté accesible');
        console.error('   URL esperada: ' + window.location.origin + '/service-worker.js');
      }

      return false;
    }
  }

  /**
   * Detener Pusher Beams
   */
  async stop() {
    if (!this.beamsClient) return;

    try {
      await this.beamsClient.stop();
      console.log('✅ Pusher Beams detenido');
      this.isInitialized = false;
      this.beamsClient = null;
      this.deviceId = null;
    } catch (error) {
      console.error('❌ Error deteniendo Pusher Beams:', error);
    }
  }

  /**
   * Obtener Device ID actual
   */
  getDeviceId() {
    return this.deviceId;
  }

  /**
   * Verificar si está listo
   */
  isReady() {
    return this.isInitialized && this.beamsClient !== null;
  }
}

export const pusherBeamsService = new PusherBeamsService();
export default pusherBeamsService;